/**
 * Preview for one file.
 *
 * The important thing this surface does is refuse to blur the line between a
 * document and its extraction. A .pptx shows what the model reads under an
 * explicitly labelled tab, next to a download of the real file — so when someone
 * asks "why did the AI say that?", the answer is one click away rather than a
 * question about what the system did to their document.
 */

import React, { useEffect, useState } from 'react';
import { api, type VfsFileEntry } from '../shared/api-client';
import { ChatMarkdownComponent } from '../chat/ChatMarkdownComponent';
import { ExportMenuComponent, type ExportMenuItem } from '../shared/components/ExportMenuComponent';
import { FileIconComponent, fileIconKindFor } from './FileIconComponent';
import { csvExcelDownloadItem, driveExportItem, exportBaseName, wordDownloadItem } from '../shared/export-actions';
import { currentReturnTo, useGoogleDriveConnection } from '../google-drive/google-drive-hooks';
import { useVfsFile } from './file-hooks';
import './FilePreviewComponent.css';

interface FilePreviewComponentProps {
  entry: VfsFileEntry | null;
  threadId?: string;
  accountId?: string;
  /** Rendered in the header — the chat drawer puts "use this file" here. */
  actions?: (entry: VfsFileEntry) => React.ReactNode;
}

type PreviewTab = 'preview' | 'source';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatModified(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function FilePreviewComponent(props: FilePreviewComponentProps): React.ReactElement {
  const { entry, threadId, accountId } = props;
  const { file, loading, error, saving, save, reload } = useVfsFile(entry?.path ?? null, threadId, accountId);
  const [tab, setTab] = useState<PreviewTab>('preview');
  const [draft, setDraft] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  // Reset the draft whenever the file changes, so an edit in progress cannot
  // leak into a different document.
  useEffect(() => { setDraft(null); setTab('preview'); setConflict(false); }, [entry?.path]);

  const driveConnection = useGoogleDriveConnection();

  if (!entry) {
    return <div className="file-preview file-preview--empty">Select a file to preview it.</div>;
  }

  const isImage = entry.mediaType.startsWith('image/');
  // Only the file's own text is editable. An extraction is a view: saving it
  // would either discard the real document or silently disagree with it.
  const editable = !entry.binary && !isImage;
  const text = draft ?? file?.text ?? '';

  const extension = entry.name.includes('.') ? `.${entry.name.split('.').pop()}` : '';
  const isCsv = extension.toLowerCase() === '.csv';

  // Only a file whose text IS the file gets format conversions. For a binary the
  // preview is an *extraction*, and building a .docx from it — or handing it to
  // Drive — would ship a paraphrase wearing the document's name. That is exactly
  // the confusion this component's header and its "extracted text" explainer
  // exist to prevent, so binaries and images get the original and nothing else.
  //
  // There is no separate "Markdown" item here even for a .md file: the original
  // download already IS that, and offering the same bytes twice under two names
  // reads as a difference that does not exist. (The artifact pane does offer one,
  // because there the markdown is a live draft with no saved original to point at.)
  const exportItems: ExportMenuItem[] = [
    {
      id: 'original',
      label: extension ? `Download original (${extension})` : 'Download original',
      href: api.getVfsDownloadUrl(entry.path, threadId, accountId),
      download: entry.name,
    },
    ...(editable
      ? [
          // A CSV the agent wrote is a spreadsheet deliverable, not a document —
          // offer the real .xlsx first, and skip the Word/Drive items that would
          // pour raw CSV text into a .docx. Those only make sense for prose.
          ...(isCsv
            ? [csvExcelDownloadItem(entry.name, () => text)]
            : [
                wordDownloadItem(entry.name, () => text),
                driveExportItem({
                  title: exportBaseName(entry.name),
                  getMarkdown: () => text,
                  connection: driveConnection,
                  returnTo: currentReturnTo(),
                }),
              ]),
        ].filter((item): item is ExportMenuItem => item !== null)
      : []),
  ];

  return (
    <div className="file-preview">
      <header className="file-preview-header">
        <div className="file-preview-heading">
          <FileIconComponent kind={fileIconKindFor(entry.kind, entry.mediaType, entry.name)} size={18} />
          <div className="file-preview-heading-text">
            <span className="file-preview-name" title={entry.path}>{entry.name}</span>
            <span className="file-preview-meta">
              {entry.kind === 'directory' ? 'Folder' : formatSize(entry.sizeBytes)}
              <span className="file-preview-meta-dot" aria-hidden="true">·</span>
              {formatModified(entry.updatedAt)}
              <span className="file-preview-meta-dot" aria-hidden="true">·</span>
              {entry.mediaType}
            </span>
          </div>
          {file?.extracted && <span className="file-preview-badge">extracted text</span>}
        </div>
        <div className="file-preview-actions">
          {props.actions?.(entry)}
          <ExportMenuComponent items={exportItems} triggerClassName="file-preview-download" align="right" />
        </div>
      </header>

      {file?.extracted && (
        <p className="file-preview-explainer">
          This is the text the assistant reads. The {entry.name.split('.').pop()?.toUpperCase()} itself
          is unchanged — download it to open the original.
        </p>
      )}

      {file?.warnings.map((warning) => (
        <p key={warning} className="file-preview-warning">{warning}</p>
      ))}

      {conflict && (
        <div className="file-preview-conflict" role="alert">
          <span className="file-preview-conflict-text">
            This file changed while you were editing it. Reload to see the current version, or overwrite it with your copy.
          </span>
          <div className="file-preview-conflict-actions">
            <button type="button" className="file-preview-conflict-button" disabled={loading} onClick={() => { setConflict(false); setDraft(null); void reload(); }}>
              {loading ? 'Reloading…' : 'Reload'}
            </button>
            <button type="button" className="file-preview-conflict-button file-preview-conflict-button--danger" disabled={saving} onClick={() => { void save(draft ?? '', { force: true }).then((r) => { if (r.ok) { setConflict(false); setDraft(null); } }); }}>
              {saving ? 'Overwriting…' : 'Overwrite'}
            </button>
          </div>
        </div>
      )}

      {editable && (
        <nav className="file-preview-tabs">
          <button type="button" className={tab === 'preview' ? 'active' : ''} onClick={() => setTab('preview')}>
            Preview
          </button>
          <button type="button" className={tab === 'source' ? 'active' : ''} onClick={() => setTab('source')}>
            Source
          </button>
          {draft !== null && draft !== file?.text && !conflict && (
            <button
              type="button"
              className="file-preview-save"
              disabled={saving}
              onClick={() => { void save(draft).then((r) => { if (r.ok) setDraft(null); else if (r.conflict) setConflict(true); }); }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </nav>
      )}

      <div className="file-preview-body">
        {loading && <p className="file-preview-status">Loading…</p>}
        {error && <p className="file-preview-error">{error}</p>}

        {isImage && (
          <img
            className="file-preview-image"
            src={api.getVfsDownloadUrl(entry.path, threadId, accountId)}
            alt={entry.name}
          />
        )}

        {!isImage && !loading && !error && (
          tab === 'source' && editable
            ? (
              <textarea
                className="file-preview-source"
                value={text}
                spellCheck={false}
                onChange={(e) => setDraft(e.target.value)}
              />
            )
            : <ChatMarkdownComponent content={text} />
        )}
      </div>
    </div>
  );
}
